import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { X, Check, Crown, Fire, ChartLineUp, Bell, CalendarCheck, Camera } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";
const TREADMILL_IMAGE = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/4u94j2us_755A0386-E611-42B8-9C89-BB24AB55A3E4.jpeg";

const PREMIUM_FEATURES = [
  { icon: CalendarCheck, text: 'Full 365-day tracking' },
  { icon: Camera, text: 'Before & After photo comparison' },
  { icon: ChartLineUp, text: 'Full analytics dashboard' },
  { icon: Fire, text: 'Streak tracking & badges' },
  { icon: Bell, text: 'Smart reminders' },
];

const Paywall = ({ onClose, onSubscribe }) => {
  const navigate = useNavigate();

  const handleStartTrial = () => {
    if (onSubscribe) onSubscribe('trial');
    else if (onClose) onClose();
  };

  const handleUnlock = () => {
    navigate('/subscription');
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl overflow-y-auto" data-testid="paywall-screen">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-[0.05] bg-center bg-no-repeat bg-cover pointer-events-none"
        style={{ backgroundImage: `url(${LOGO_URL})` }}
      />

      {/* Close button */}
      {onClose && (
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-card flex items-center justify-center z-10"
          data-testid="close-paywall-btn"
        >
          <X weight="bold" className="w-5 h-5 text-foreground" />
        </button>
      )}

      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative z-10">
        <div className="max-w-md w-full text-center">
          {/* Crown Icon */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Crown weight="fill" className="w-10 h-10 text-black" />
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl font-black text-foreground mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            See Your Transformation
          </h1>
          <h2 className="text-xl text-[#D4AF37] font-semibold mb-6">Before & After</h2>

          {/* Subtext */}
          <div className="space-y-2 mb-8">
            <p className="text-muted-foreground">Stay consistent for 365 days</p>
            <p className="text-muted-foreground">Track real progress</p>
            <p className="text-muted-foreground">Build unstoppable habits</p>
          </div>

          {/* Preview Image */}
          <div className="relative rounded-2xl overflow-hidden mb-8">
            <img 
              src={TREADMILL_IMAGE} 
              alt="Transformation preview" 
              className="w-full h-48 object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <p className="text-foreground font-semibold">Unlock your full potential</p>
            </div>
          </div>

          {/* Premium Features */}
          <div className="premium-card p-5 mb-8 text-left">
            <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4 text-center">Premium Includes</h3>
            <div className="space-y-3">
              {PREMIUM_FEATURES.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Icon weight="duotone" className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <span className="text-foreground text-sm">{feature.text}</span>
                    <Check weight="bold" className="w-4 h-4 text-green-500 ml-auto" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-6">
            <p className="text-muted-foreground text-sm mb-2">Starting at</p>
            <p className="text-4xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              $9.99<span className="text-lg text-muted-foreground font-normal">/month</span>
            </p>
            <p className="text-green-500 text-sm mt-1">or $59.99/year (Save 50%)</p>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleStartTrial}
              className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest rounded-full py-6 hover:bg-[#F3E5AB] transition-all gold-glow gold-glow-hover"
              data-testid="start-trial-btn"
            >
              Start 7-Day Free Trial
            </Button>

            <Button
              onClick={handleUnlock}
              variant="outline"
              className="w-full border-[#D4AF37]/30 text-foreground rounded-full py-6 hover:bg-[#D4AF37]/5"
              data-testid="unlock-access-btn"
            >
              View Plans & Pricing
            </Button>
          </div>

          {/* Testimonial placeholder */}
          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-muted-foreground text-sm italic">
              "Track365 helped me stay consistent for the first time in my life. 120 days and counting!"
            </p>
            <p className="text-xs text-[#D4AF37] mt-2">— Sarah M., Track365 User</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
